import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useBranding } from '../contexts/BrandingContext';
import { Alert, Button, Input } from '../components/ui';
import { FormItem } from '../components/ui/Form/Form';
import headerLogo from '../assets/login/header-logo.png';
import headerCredits from '../assets/login/header-credits.png';
import '../styles/login-v2.css';

const EMAIL = /^\S+@\S+\.\S+$/;

/**
 * Login page v2 (design_handoff_login_v2). Every admin-controlled value still
 * drives it; the design's own values are only the fallbacks:
 * - header: the header image (LOGIN_HEADER_URL, else the designed lockup) at
 *   the header's full width, or the platform logo + a drawn line when
 *   LOGIN_USE_DESIGN_LOCKUP is off;
 * - background, welcome heading and the three chips: LOGIN_BACKGROUND_URL,
 *   LOGIN_WELCOME_*, LOGIN_FEATURE_*_* — empty = the design's own
 *   (Settings → Branding → «صفحة تسجيل الدخول»);
 * - system name: SYSTEM_TITLE_AR / SYSTEM_TITLE_EN, above the card title;
 * - brand colour: BRAND_PRIMARY_COLOR (button, focus ring, language toggle);
 * - «إنشاء حساب»: ENABLE_USER_REGISTRATION;
 * - first-visit language: DEFAULT_LANGUAGE (applied by BrandingContext).
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language, setLanguage, isRTL, t } = useLanguage();
  const { branding } = useBranding();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const emailInvalid = touched && !EMAIL.test(formData.email);
  const passwordMissing = touched && !formData.password;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTouched(true);
    if (!EMAIL.test(formData.email) || !formData.password) return;
    setLoading(true);

    try {
      await login({
        email: formData.email,
        password: formData.password,
      });
      // Redirect to dashboard (Dashboard component will handle role-based rendering)
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const systemName = (isRTL ? branding.system_title_ar : branding.system_title_en)
    || branding.system_title
    || t('loginSystemName');
  const useLockup = branding.login_design_lockup !== false || !branding.logo_url;
  const welcome = (isRTL ? branding.login_welcome_ar : branding.login_welcome_en) || t('welcomeBack');
  const customFeatures = (isRTL ? branding.login_features_ar : branding.login_features_en) || [];
  const features = ['secureAccess', 'realTimeTracking', 'teamCollaboration']
    .map((key, i) => customFeatures[i] || t(key));

  return (
    <div className="lv2" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className={`lv2-header${useLockup ? '' : ' is-custom'}`}>
        {useLockup ? (
          branding.login_header_url ? (
            <img className="lv2-lockup" src={branding.login_header_url} alt={branding.company_name || 'MDC'} />
          ) : (
            // The designed lockup from its parts, so the credits stay legible
            <div className="lv2-lockup-split" dir="ltr" role="img" aria-label={branding.company_name || 'MDC'}>
              <img className="lv2-lockup-credits" src={headerCredits} alt="" />
              <span className="lv2-lockup-line" />
              <img className="lv2-lockup-logo" src={headerLogo} alt="" />
            </div>
          )
        ) : (
          <div className="lv2-custom">
            <div className="lv2-custom-line" />
            <img className="lv2-custom-logo" src={branding.logo_url} alt={branding.company_name} />
          </div>
        )}
      </header>

      <div className="lv2-stage">
        <div
          className="lv2-pattern"
          aria-hidden="true"
          style={branding.login_background_url ? { backgroundImage: `url("${branding.login_background_url}")` } : undefined}
        />

        <main className="lv2-main">
          <form className="lv2-card" onSubmit={handleSubmit} noValidate>
            <div className="lv2-card-top">
              <div className="lv2-heading">
                {systemName && <span className="lv2-eyebrow">{systemName}</span>}
                <h1>{t('loginTitle')}</h1>
                <p>{t('loginSubtitle')}</p>
              </div>
              <div className="lv2-lang" dir="ltr" role="group" aria-label="Language / اللغة">
                {(['ar', 'en'] as const).map(lang => (
                  <button
                    key={lang}
                    type="button"
                    className={language === lang ? 'is-active' : ''}
                    aria-pressed={language === lang}
                    onClick={() => setLanguage(lang)}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <Alert
                variant="error"
                className="lv2-alert"
                icon={(
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5M12 16.5v.01" />
                  </svg>
                )}
              >
                <bdi>{error}</bdi>
              </Alert>
            )}

            <div className="lv2-fields">
              <FormItem
                className="lv2-field"
                label={t('loginEmail')}
                htmlFor="email"
                error={emailInvalid ? t('loginEmailInvalid') : undefined}
              >
                <Input
                  id="email"
                  name="email"
                  type="email"
                  dir="ltr"
                  autoComplete="email"
                  className="lv2-input is-email"
                  invalid={emailInvalid}
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </FormItem>

              <FormItem className="lv2-field" error={passwordMissing ? t('loginPasswordRequired') : undefined}>
                <div className="lv2-label-row">
                  <label className="ecme-form-label" htmlFor="password">{t('password')}</label>
                  <Link to="/forgot-password" className="lv2-forgot">{t('loginForgot')}</Link>
                </div>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="lv2-input"
                  invalid={passwordMissing || !!error}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  suffix={(
                    <button
                      type="button"
                      className="lv2-eye"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('loginHidePassword') : t('loginShowPassword')}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 3l18 18" />
                          <path d="M10.6 5.1A10 10 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-3 3.9M6.6 6.6A17 17 0 0 0 2.5 12S6 19 12 19a9.6 9.6 0 0 0 4.4-1.1" />
                          <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  )}
                />
              </FormItem>

            </div>

            <Button type="submit" variant="solid" block loading={loading} className="lv2-submit">
              {loading ? t('loginSigningIn') : t('loginTitle')}
            </Button>

            {/* Hidden when self-registration is disabled */}
            {branding.registration_enabled !== false && (
              <p className="lv2-signup">
                {t('dontHaveAccount')} <Link to="/register">{t('createAccount')}</Link>
              </p>
            )}
          </form>

          <div className="lv2-aside">
            <h2>{welcome}</h2>
            <div className="lv2-chips">
              {features.map((feature, i) => <span key={i} className="lv2-chip">{feature}</span>)}
            </div>
          </div>
        </main>

        <footer className="lv2-footer">
          <bdi dir="ltr">© {new Date().getFullYear()} MDC</bdi>
        </footer>
      </div>
    </div>
  );
};

export default Login;
